// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const { TYPES: modelTypes } = require('shared/model-types')
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const ArticleCard = require('client-v2/common/components/cards/article')
const CollectionCard = require('client-v2/common/components/cards/collection')
const UserCard = require('client-v2/common/components/cards/user')

const bem = BemEntity('CardGrid')

class CardGrid extends React.PureComponent {
  constructor(props) {
    super(props)

    this.gridRef = React.createRef()
  }

  wrapChildren(children) {
    return React.Children.map(children, child => {
      return React.cloneElement(child, {
        className: classNames(
          bem.element('item', 'child'),
          child.className
        )
      })
    })
  }

  renderArticleCard({ type, id, article, meta, pageContext }, index) {
    return (
      tag(this.props.articleCardComponent || ArticleCard, {
        key: `${type}:${id}`,
        // Flags
        isHrefsEnabled: this.props.isHrefsEnabled,
        // Values
        article,
        className: bem.element('item'),
        itemIndex: index,
        pageContext,
        portalContainer: this.gridRef,
        recReason: meta.recReason,
        recPosition: meta.recPosition,
        // Callbacks
        onAddClick: this.props.onArticleAddClick,
        onCardClick: this.props.onArticleCardClick,
        onCountClick: this.props.onArticleCountClick,
        onDomainClick: this.props.onArticleDomainClick,
        onEmbedDisclose: this.props.onArticleEmbedDisclose,
        onMoreClick: this.props.onArticleMoreClick,
        onPreviewClick: this.props.onArticlePreviewClick,
        onSeen: this.props.onArticleSeen,
        onShareClick: this.props.onArticleShareClick
      })
    )
  }

  renderCollectionCard({ type, id, collection, meta, pageContext }) {
    return (
      tag(this.props.collectionCardComponent || CollectionCard, {
        key: `${type}:${id}`,
        // Flags
        isHrefsEnabled: this.props.isHrefsEnabled,
        isPostCountVisible: this.props.isCollectionPostCountVisible,
        isSelf: collection['user_id'] === this.props.selfUserId,
        isSubscriberCountVisible: this.props.isCollectionSubscriberCountVisible,
        // Values
        className: bem.element('item'),
        collection,
        pageContext,
        recReason: meta.recReason,
        // Callbacks
        onCardClick: this.props.onCollectionCardClick,
        onPostCountClick: this.props.onCollectionPostCountClick,
        onShareClick: this.props.onCollectionShareClick,
        onSubscribeClick: this.props.onCollectionSubscribeClick,
        onSubscriberCountClick: this.props.onCollectionSubscriberCountClick,
        onUserClick: this.props.onCollectionUserClick
      })
    )
  }

  renderUserCard({ type, id, user, meta, pageContext }) {
    return (
      tag(this.props.userCardComponent || UserCard, {
        key: `${type}:${id}`,
        // Flags
        isFollowerCountVisible: this.props.isUserFollowerCountVisible,
        isHrefsEnabled: this.props.isHrefsEnabled,
        isSelf: user.id === this.props.selfUserId,
        // Values
        className: bem.element('item'),
        pageContext,
        recReason: meta.recReason,
        user,
        // Callbacks
        onCardClick: this.props.onUserCardClick,
        onFollowClick: this.props.onUserFollowClick,
        onFollowerCountClick: this.props.onUserFollowerCountClick
      })
    )
  }

  renderItems() {
    return this.props.items.map((item, index) => {
      switch (item.type) {
        case modelTypes.ARTICLE:
          return this.renderArticleCard(item, index)
        case modelTypes.MIX:
          return this.renderCollectionCard(item, index)
        case modelTypes.USER:
          return this.renderUserCard(item, index)
        case 'render':
          return this.wrapChildren(item.render(item, index))
        default:
          return null
      }
    })
  }

  renderLeading() {
    const { renderLeading } = this.props
    if (renderLeading) {
      return this.wrapChildren(renderLeading())
    }
  }

  renderTrailing() {
    const { renderTrailing } = this.props
    if (renderTrailing) {
      return this.wrapChildren(renderTrailing())
    }
  }

  render() {
    const { props } = this
    return (
      tag('div', {
        ref: this.gridRef,
        className: classNames(
          props.className,
          bem.block()
        )
      },
        this.renderLeading(),
        this.renderItems(),
        this.renderTrailing()
      )
    )
  }
}

CardGrid.defaultProps = {
  isCollectionPostCountVisible: true,
  isCollectionSubscriberCountVisible: true,
  isHrefsEnabled: true,
  isUserFollowerCountVisible: true
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')
  const { FeedItemRecord } = require('client-v2/common/containers/feed/records')

  CardGrid.propTypes = {
    // Flags
    isCollectionPostCountVisible: PropTypes.bool,
    isCollectionSubscriberCountVisible: PropTypes.bool,
    isHrefsEnabled: PropTypes.bool,
    isUserFollowerCountVisible: PropTypes.bool,
    // Values
    className: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape(FeedItemRecord.propTypes)
    ).isRequired,
    selfUserId: PropTypes.number,
    // Components
    articleCardComponent: PropTypes.elementType,
    collectionCardComponent: PropTypes.elementType,
    userCardComponent: PropTypes.elementType,
    // Render props
    renderLeading: PropTypes.func,
    renderTrailing: PropTypes.func,
    // Callbacks
    onArticleAddClick: PropTypes.func,
    onArticleCardClick: PropTypes.func,
    onArticleCountClick: PropTypes.func,
    onArticleDomainClick: PropTypes.func,
    onArticleEmbedDisclose: PropTypes.func,
    onArticleMoreClick: PropTypes.func,
    onArticlePreviewClick: PropTypes.func,
    onArticleSeen: PropTypes.func,
    onArticleShareClick: PropTypes.func,
    onCollectionCardClick: PropTypes.func,
    onCollectionPostCountClick: PropTypes.func,
    onCollectionShareClick: PropTypes.func,
    onCollectionSubscribeClick: PropTypes.func,
    onCollectionSubscriberCountClick: PropTypes.func,
    onCollectionUserClick: PropTypes.func,
    onUserCardClick: PropTypes.func,
    onUserFollowClick: PropTypes.func,
    onUserFollowerCountClick: PropTypes.func
  }
}

module.exports = CardGrid
